/**
 * @flow
 * @format
 */

import { slice, get } from 'lodash';
import { set } from 'lodash/fp';

import { getStepByField, StepOrder } from '~/utils/stepHelpers';
import { StepState, Steps } from '~/utils/constants';

import { prevStep, nextStep } from '~/selectors/design';
import { constants } from '~/actions/design';
import appRoutines from '~/routines/global';
import designRoutines from '~/routines/design';

import type { ProgressState, ValidStatusType } from '~/actions/progress';

// import debug from '~/utils/macros/debug.macro';

const { NOT_STARTED, ERROR, COMPLETE, DISABLED } = StepState;

export type StatusType = { id: string, status: ValidStatusType };

type ActionType = {
  payload: {
    content?: string,
    field: string,
    message: Object,
    messageFor?: string,
  },
  type: string,
};

type StateType = ProgressState;

/**
 * Helper function to create a StateType[] of given ids and status
 *
 * @param {string[]} list
 * @param {ValidStatusType} status
 * @returns {StatusType[]}
 */
function updateStatus(list: Array<*>, status: ValidStatusType): {} {
  return list.reduce(
    (obj, id) => ({
      ...obj,
      [id]: status,
    }),
    {},
  );
}

/**
 * Returns a StatusType[] of steps before the given step.
 *
 * @param {string} step
 * @returns {StatusType[]}
 */
function getPrevSteps(step: string): any {
  const index = StepOrder.findIndex(id => id === step);
  return slice(StepOrder, 0, index);
}

/**
 * Returns a StatusType[] of steps after the given step.
 *
 * @param {string} step
 * @returns {StatusType[]}
 */
function getNextSteps(step: ?string): any {
  const index = StepOrder.findIndex(id => id === step);
  return slice(StepOrder, index);
}

export const initialState: StateType = {
  active: StepOrder[0],
  hasError: false,
  status: updateStatus(StepOrder, NOT_STARTED),
  info: {},
  stepProgress: {},
};

/**
 * StepProgress reducer
 *
 * @export
 * @param {StateType} [state=initialState]
 * @param {ActionType} action
 * @returns {StateType}
 */
export default function(
  state: StateType = initialState,
  action: ActionType,
): any {
  switch (action.type) {
    case appRoutines.jumpToStep.toString(): {
      const newStep: any = action.payload;
      const prevSteps = getPrevSteps(newStep);
      const completed = updateStatus(prevSteps, COMPLETE);

      /**
       * Jumping a step should do the following:
       * Set current step
       * If step is not started and no error - set all previous steps as completed
       * if step is completed - do nothing
       */
      return {
        ...state,
        status: {
          ...state.status,
          ...completed,
        },
        active: newStep,
      };
    }

    case constants.STEP_FORWARD: {
      const next = nextStep(state.active);
      const prevSteps = getPrevSteps(next);
      const completed = updateStatus(prevSteps, COMPLETE);
      return {
        ...state,
        status: {
          ...state.status,
          ...completed,
        },
        active: next,
      };
    }

    case constants.STEP_BACKWARD: {
      const next = prevStep(state.active);
      return {
        ...state,
        active: next,
      };
    }

    case constants.NEW_DESIGN: {
      return {
        ...initialState,
      };
    }

    case constants.AUTO_COMPLETE: {
      // Autocomplete should jump the user to loss review
      const completed = updateStatus(StepOrder, COMPLETE);
      return {
        status: {
          ...completed,
        },
        active: Steps.STEP_ANALYSIS,
      };
    }

    case constants.VALIDATION_ERROR: {
      const { field } = action.payload;
      const errorStep = getStepByField(field);

      // set error on `field` and set the remaining steps to disabled.
      // return set(state, ['status', errorStep], ERROR);
      const nextSteps = getNextSteps(errorStep);
      const disabled = updateStatus(nextSteps, DISABLED);

      if (!errorStep) return state;

      return {
        ...state,
        // info: set(state, ['info', errorStep], message),
        info: set([errorStep], field, state.info),
        status: {
          ...state.status,
          ...disabled,
          [errorStep]: ERROR,
        },
      };
    }

    case constants.UPDATE: {
      const { message } = action.payload;

      const type = get(action, ['payload', 'type']) || get(message, ['type']);
      const { messageFor } = action.payload;

      const messageForStep = messageFor && getStepByField(messageFor);
      const step = messageForStep || state.active;
      const prevSteps = getPrevSteps(step);
      const nextSteps = getNextSteps(step);
      const completed = updateStatus(prevSteps, COMPLETE);

      // debug(messageFor, messageForStep, prevSteps, nextSteps);

      if (type === 'SUCCESS') {
        const notStarted = updateStatus(nextSteps, NOT_STARTED);

        return {
          ...state,
          status: {
            ...state.status,
            ...notStarted,
            ...completed,
            [step]: NOT_STARTED,
          },
        };
      } else if (type === 'ERROR' || type === 'INFO') {
        /**
         * In the event of an error:
         *   Set all following steps to 'disabled'
         *   Set step where error occured to 'error'
         */

        /**
         * the steps between current and field should be NOT_STARTED.
         * Fixes the cases where an error occurs >1 steps away.
         */
        const prevStepFix = updateStatus(
          slice(prevSteps, prevSteps.indexOf(state.active)),
          NOT_STARTED,
        );

        const disabled = updateStatus(nextSteps, DISABLED);

        return {
          ...state,
          // info: set(state.info, [field], [content]),
          info: set([step], messageFor, state.info),
          // info: Object.merge({}, state.info, { [field]: {}})
          status: {
            ...state.status,
            ...disabled,
            ...prevStepFix,
            [state.active]: NOT_STARTED, // Sometimes an error occurs else where - active != field
            [step]: ERROR,
          },
        };
      }

      return state;
    }

    case constants.STEP_RESET: {
      // We need to reset any stages after this one
      const next = nextStep(state.active);
      const nextSteps = getNextSteps(next);
      const notStarted = updateStatus(nextSteps, NOT_STARTED);

      return {
        ...state,
        status: {
          ...state.status,
          ...notStarted,
          [state.active]: NOT_STARTED,
        },
      };
    }

    // TODO: Open to last changed step
    case designRoutines.fetchRecord.REQUEST: {
      return {
        ...state,
        active: Steps.STEP_ONE,
        status: {
          [Steps.STEP_ONE]: StepState.STARTED,
        },
      };
    }

    default:
      return state;
  }
}



// WEBPACK FOOTER //
// ./src/reducers/progress.js